import React, { useRef, useState } from 'react';

import type {
  ByfunnPromoArticle,
  GodtTeasers,
  Recommendations,
} from '../../../types/frapi-types.js';
import type { SaveListItem } from '../../../types/save-types.js';
import type { SiteVersion } from '../../../../core/system/rendering/types.js';
import type { OptionalDataFailed } from '../../../../core/fetching/index.js';

import { withByfunnPulse, LinkWithPulse } from '../../../pulse/index.js';
import { Salesposter } from '../../components/articles/articleComponents/Salesposter/Salesposter.js';
import { LoginBar } from '../../components/articles/articleComponents/Salesposter/LoginBar.js';
import Navigation from '../../components/commonComponents/Navigation/Navigation.js';
import Footer from '../../components/commonComponents/Footer.js';
import { DataController } from '../../components/commonComponents/DataController.js';
import {
  getLead,
  isRestaurant,
  isRestaurantReview,
  isReview,
  sectionMapping,
  topSaveTags,
} from '../../components/articles/helpers.js';
import { useGetSaved } from '../../hooks/useGetSaved.js';
import { Save } from '../../components/saving/Save.js';
import { Intro } from '../../components/articles/GuideArticle/Intro.js';
import Image from '../../components/articles/articleComponents/Image.js';
import About from '../../components/articles/articleComponents/About/index.js';
import Text from '../../components/articles/articleComponents/Text/index.js';
import Authors from '../../components/articles/articleComponents/Authors.js';
import { Recommended } from '../../components/articles/articleComponents/Recommended.js';
import { Newsletter } from '../../components/newsletter/Newsletter.js';
import { ListsProvider } from '../../components/saving/ListsProvider.js';

type ArticlePagePromoProps = {
  article: ByfunnPromoArticle;
  recommendations: Recommendations | OptionalDataFailed;
  godtTeasers: GodtTeasers | OptionalDataFailed;
  isMobile?: boolean;
  siteVersion?: SiteVersion;
  referrer?: string;
  isHermesApp?: boolean;
  enableRuterCampaign: boolean;
};

const ArticlePagePromo: React.FC<ArticlePagePromoProps> = ({
  article,
  recommendations,
  isMobile,
  siteVersion,
  referrer,
  isHermesApp,
  godtTeasers,
  enableRuterCampaign,
}) => {
  const review = isReview(article);
  const isRestaurantSection = isRestaurant(article);

  const [overlay, toggleOverlay] = useState(false);
  const [savedItems] = useGetSaved();

  const articleElementRef = useRef(null);

  const handleOverlay = () => {
    toggleOverlay(!overlay);
  };

  const lead = getLead(article);
  const title = article.title;

  const teaserToSave: SaveListItem = {
    id: article.id,
    title,
    imageId: article.mainImage?.id,
    slug: article.slug,
    kicker: review ? topSaveTags(article) || article.section : article.section,
  };
  const { previewText } = article;

  const Guide = () => (
    <div className={isMobile ? 'mobile-article' : 'desktop'}>
      <Navigation
        fullWidth={!isMobile}
        activeSection={sectionMapping[article.section]}
        isHermesApp={isHermesApp}
      />
      <Intro
        title={title}
        lead={lead}
        handleOverlay={handleOverlay}
        item={teaserToSave}
        mainImage={article.mainImage}
        withIllustration={article.teaserWithIllustration}
        colorPalette={article.colorPalette}
        savedItems={savedItems}
      />
      <div className="article-container-promo">
        <Authors
          authors={article.authors}
          published={article.published}
          updated={article.updated}
          section={sectionMapping[article.section]}
        />
        <Newsletter />
        {previewText && <Text type="text" paragraphs={previewText} />}
      </div>
    </div>
  );
  const Review = () => (
    <>
      <Navigation
        fullWidth={!isMobile}
        activeSection={sectionMapping[article.section]}
        isTransparent
        isHermesApp={isHermesApp}
      />
      {article.mainImage && (
        <div className="front-image-wrapper">
          <Image
            imageAsset={article.mainImage}
            hideByline
            fullWidth
            articleTitle={title}
          />
        </div>
      )}

      <div className="article-container">
        <div className="about-wrapper">
          <div className="no-abo-no-info">
            Oppsummeringen er for abonnenter
            <div className="pointer">Les et utdrag</div>
          </div>
          <About
            article={article}
            handleOverlay={handleOverlay}
            item={teaserToSave}
            isRestaurantReview={isRestaurantReview(article)}
            enableRuterCampaign={enableRuterCampaign}
          />
        </div>
        {title && <h1 className="title">{title}</h1>}
        {lead && article.lead && <Text {...article.lead} />}
        <Authors
          section={sectionMapping[article.section]}
          authors={article.authors}
          published={article.published}
          updated={article.updated}
        />
        <Newsletter />
        {previewText && <Text type="text" paragraphs={previewText} />}
      </div>
    </>
  );

  const classNames = [
    review ? 'review-article' : 'guide-article',
    isMobile ? 'mobile-article' : 'desktop-article',
    'promo',
    siteVersion,
  ].join(' ');

  return (
    <ListsProvider>
      <div ref={articleElementRef} className={classNames}>
        {overlay && <Save handleOverlay={handleOverlay} item={teaserToSave} />}
        {review ? <Review /> : <Guide />}
        <LoginBar />
        <div className="promo-wrapper paywall">
          {!isHermesApp && siteVersion && (
            <Salesposter
              siteVersion={siteVersion}
              id={article.id}
              referrer={referrer}
              article={article}
              articleRef={articleElementRef}
            />
          )}

          {'items' in recommendations && (
            <Recommended
              recommendations={recommendations}
              isMobile={!!isMobile}
              godtTeasers={godtTeasers}
              isRestaurantSection={isRestaurantSection}
            />
          )}
          <LinkWithPulse
            className="homescreen-button"
            href="/"
            objectId="redirect-to-homescreen"
            name="Redirect to homescreen"
          >
            Gå til Vink-forsiden
          </LinkWithPulse>
          <Footer isMobile={isMobile} />
        </div>
        <DataController />
      </div>
    </ListsProvider>
  );
};

const ArticlePagePromoWithPulse = withByfunnPulse(ArticlePagePromo);

export { ArticlePagePromoWithPulse as ArticlePagePromo };
